import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { browserHistory } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers/plural';
import { ListRepositoriesQueryValidator } from '../../../../../shared/types/routes/vcs/githubApp';
import { DEFAULT_VCS_APP } from '../../../../../shared/vcs/githubApp/apps';
import { useQuery } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';
import { errorMessageFromResponse } from '../../../../helpers/errors';
import { useShouldShowQuickImportScanMethods } from '../../../../helpers/features/quickImportScanMethods';
import {
  useUpdateOrganizationQuickImportScanMethodsSettings,
} from '../../../../hooks/api/organization/useUpdateOrganizationQuickImportScanMethodsSettings';
import useTeams from '../../../../hooks/api/teams/useTeams';
import { useAllGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useAllGitHubAppRepositories';
import { useGitHubAppInstallations } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallations';
import { useGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useGitHubAppRepositories';
import { useImportGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useImportGitHubAppRepositories';
import { useStartGitHubAppConnect } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppConnect';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import Button from '../../../generic/Button/Button';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import { icons } from '../../../generic/Icon/icons';
import Loader from '../../../generic/Loader';
import { teamsUserCanAddProject } from '../../SelectTeam';
import ModalTeamAssignment from '../ModalTeamAssignment';
import QuickImportScanMethodsModal from '../QuickImportScanMethodsModal';
import type { FossabotPRSort } from '../../../../../shared/fossabot/common';
import type { Team } from '../../../../../shared/model-typings/team';
import type { ImportRepository, VcsRepository } from '../../../../../shared/types/routes/vcs/githubApp';
import type { VcsApp } from '../../../../../shared/vcs/githubApp/apps';
import type { Filter, Repository, ServiceFilter } from '../../../../types';
import type { OnBranchChangeFn } from '../HybridProjectsTable/types';
import type { ScanMethodProjectOverrides, ScanMethodSelections } from '../types';

import { openSingleImportedProject } from './openSingleImportedProject';
import { VcsConnectAccountModal } from './VcsConnectAccountModal';
import { VcsImportAllModal } from './VcsImportAllModal';
import { VcsImportHeader } from './VcsImportHeader';
import { VcsImportSidebar } from './VcsImportSidebar';
import { VcsInstallPanel } from './VcsInstallPanel';
import { isRepoSelectable, VcsRepositoryList } from './VcsRepositoryList';
import type { VcsImportAllOptions } from './VcsImportAllModal';

import styles from './VcsImportPage.module.less';

const PAGE_SIZE = 10;
/** Newest push first; the sort menu's first option. */
const DEFAULT_SORT: FossabotPRSort = 'newest';
const TOO_MANY_REQUESTS_STATUS = 429;
// Matches the legacy import flow: pause between keystrokes before searching server-side.
const SEARCH_DEBOUNCE_MS = 750;
// The account menu selects a connection, so every account item is keyed on connectionId.
const ACCOUNT_FILTER_KEY = 'connectionId';
// Group accounts under human-readable category headers (matching the legacy org/user grouping).
const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  Organization: 'Organizations',
  User: 'Users',
  Enterprise: 'Enterprises',
};
// The tab title names the import source per the active account's app.
const PAGE_TITLE: Record<VcsApp, string> = {
  fossabot: 'Import From fossabot',
  fossaCore: 'Import From GitHub',
};

/**
 * `useGitHubAppRepositories` types its error as `Error`, but a failed request actually rejects
 * with the response object itself (see `redux/api`), which carries a numeric `status` instead.
 */
const hasStatus = (error: unknown): error is { status: number } =>
  _.isObject(error) && 'status' in error && _.isNumber(error.status);

/**
 * `VcsRepository` mirrors the legacy `Repository` field names for reuse, so vcs rows can flow
 * straight through the list. `description` is the one wire-shape difference (`null` here,
 * optional there), and it is display-only, so dropping the null is all the reuse needs.
 */
const toRepository = ({ description, ...repo }: VcsRepository): Repository => ({
  ...repo,
  description: description ?? undefined,
});

/** Full-panel loader shown while an import is in flight, mirroring the legacy import screen. */
const ImportingLoader: VoidFunctionComponent<{ count: number }> = ({ count }) => (
  <div className={styles.statusCard}>
    <Loader color="#d2b690" size={80} />
    <h2>Importing {count} {plural(count, 'Project')}</h2>
    <p>This may take a few minutes...</p>
  </div>
);

/**
 * Terminal state after a batch is handed to the background bulk job, mirroring the legacy bulk
 * import card: the projects appear on the dashboard as the job works through them.
 */
const ImportQueuedCard: VoidFunctionComponent<{ count: number }> = ({ count }) => (
  <div className={styles.statusCard}>
    <h2>Importing {count} {plural(count, 'Project')}</h2>
    <p>This may take several minutes...</p>
    <Button icon={icons.arrowLeft} onClick={() => browserHistory.push('/projects')}>
      Back to projects
    </Button>
  </div>
);

/**
 * The vcs-service import page: an org installs the fossabot GitHub App here (step one of import,
 * not a settings action), then browses and imports repositories from its connected accounts.
 * The accounts come from the org's single import source: the fossaCore read app for orgs
 * migrated off the legacy GitHub App path, the fossabot app for everyone else.
 *
 * It owns the page chrome as well as the flow, standing apart from the legacy import shell:
 * the header and sidebar show in both states, while the main panel swaps the install pitch for
 * the repository list once the app is installed. The listing paginates and searches server-side.
 */
export const VcsImportPage: VoidFunctionComponent = () => {
  /*
   * An org imports through exactly one vcs app. A migrated org (any fossaCore read-app
   * connection) imports through the read app; fossabot connections it may also hold (e.g.
   * installed from the issues pages for PR fixes) do not appear here, so an org's projects
   * never split across import sources. Orgs without a fossaCore connection use the fossabot
   * flow, unchanged.
   */
  const fossabotInstallations = useGitHubAppInstallations('fossabot');
  const fossaCoreInstallations = useGitHubAppInstallations('fossaCore');
  const isAccountListLoading = fossabotInstallations.isLoading || fossaCoreInstallations.isLoading;
  const isAccountListError = fossabotInstallations.isError || fossaCoreInstallations.isError;
  const hasFossabotAccounts = (fossabotInstallations.data?.results.length ?? 0) > 0;
  const hasFossaCoreAccounts = (fossaCoreInstallations.data?.results.length ?? 0) > 0;
  const activeApp: VcsApp = hasFossaCoreAccounts ? 'fossaCore' : DEFAULT_VCS_APP;
  const installations =
    (hasFossaCoreAccounts ? fossaCoreInstallations : fossabotInstallations).data?.results ?? [];
  const isInstalled = installations.length > 0;

  /*
   * "Install on GitHub" (and the menu's "Install GitHub App") adds an account to the org's import
   * source, so it targets the active app: the read app for migrated orgs, fossabot otherwise. It
   * starts with a GitHub authorization rather than GitHub's install page: an account another FOSSA
   * org already installed on makes that page a dead end, and the user cannot know that in advance.
   * The callback either lands back here with ?connect=ready|error, which opens the modal listing
   * accounts to connect, or continues to GitHub's install page when there is nothing to connect.
   * The modal's "Install on another account" is the direct install for a fresh account.
   */
  const startConnect = useStartGitHubAppConnect();
  const startInstall = useStartGitHubAppInstall();
  const onInstallGithubApp = () => startConnect.mutate(activeApp);
  const onInstallAnotherAccount = () => startInstall.mutate(activeApp);

  // Search, sort and paging live in the URL (?search=&sort=&page=&pageSize=) so a return visit
  // lands on the same list. Defaults are left out of the URL to keep it clean.
  const [query, setQuery] = useQuery<{
    search?: string; sort?: string; page?: string; pageSize?: string; connect?: string;
  }>();
  // Read once on mount: the URL sync effect below drops the flag straight away.
  const [connectStatus, setConnectStatus] = useState(
    query.connect === 'ready' || query.connect === 'error' ? query.connect : null,
  );
  const [searchValue, setSearchValue] = useState(_.isString(query.search) ? query.search : '');
  const [sort, setSort] = useState<FossabotPRSort>(query.sort === 'oldest' ? 'oldest' : DEFAULT_SORT);
  const [page, setPage] = useState(Math.max(1, _.toInteger(query.page) || 1));
  /*
   * Run the deep link through the endpoint's own validator so an out-of-range ?pageSize= falls
   * back to the default instead of reaching the endpoint and failing the listing.
   */
  const parsedPageSize = ListRepositoriesQueryValidator.shape.pageSize.safeParse(query.pageSize);
  const [pageSize, setPageSize] = useState(parsedPageSize.success ? parsedPageSize.data : PAGE_SIZE);
  const [selectedRepos, setSelectedRepos] = useState<Repository[]>([]);
  // "Select all" enumerates the account client-side (like import-all); true once that selection landed.
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [branchOverrides, setBranchOverrides] = useState<Record<string, string>>({});
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  // Defaults on, matching the legacy import page's badge-PR checkbox.
  const [shouldSubmitBadgePRs, setShouldSubmitBadgePRs] = useState(true);
  const [isImportAllModalOpen, setIsImportAllModalOpen] = useState(false);
  const { debouncedValue: q } = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setQuery({
      ...(q ? { search: q } : {}),
      ...(sort !== DEFAULT_SORT ? { sort } : {}),
      ...(page > 1 ? { page: String(page) } : {}),
      ...(pageSize !== PAGE_SIZE ? { pageSize: String(pageSize) } : {}),
    });
  }, [q, sort, page, pageSize, setQuery]);

  // Validated against the current list so a stale pick (e.g. the account was just disconnected)
  // falls back to the first connected account automatically.
  const activeConnectionId =
    installations.find((account) => account.connectionId === selectedConnectionId)?.connectionId ??
    installations[0]?.connectionId ??
    '';
  const activeAccount = installations.find((account) => account.connectionId === activeConnectionId);

  // Accounts grouped by type (Organizations / Users / …), matching legacy. The menu also hosts
  // the "Install GitHub App" action, so it renders even with a single connected account.
  const accountsByType = _.groupBy(installations, (account) => account.accountType);
  const filters: ServiceFilter[] = Object.entries(accountsByType).map(([accountType, accounts]) => ({
    category: ACCOUNT_TYPE_LABELS[accountType] ?? accountType,
    key: ACCOUNT_FILTER_KEY,
    filters: accounts.map((account) => ({
      name: account.accountName,
      value: account.connectionId,
      icon: account.avatarUrl ?? '',
    })),
  }));
  const selectedFilter: Filter | undefined = activeAccount
    ? {
        name: activeAccount.accountName,
        value: activeAccount.connectionId,
        icon: activeAccount.avatarUrl ?? '',
      }
    : undefined;

  // A new search, sort or account switch starts over from the first page, matching the legacy import flow.
  // An "all" selection was scoped to the old search, so it stops being "all" too. The first
  // settled search + account (the URL's) is not a change, so a ?page= deep link survives mount.
  const listScopeRef = useRef<string | null>(null);
  useEffect(() => {
    if (isAccountListLoading) {
      return;
    }
    const scope = `${activeConnectionId}:${sort}:${q}`;
    if (listScopeRef.current !== null && listScopeRef.current !== scope) {
      setPage(1);
      setAreAllSelected(false);
      setIsSelectingAll(false);
    }
    listScopeRef.current = scope;
  }, [q, sort, activeConnectionId, isAccountListLoading]);

  // A repo selection (and any branch picks) is scoped to one account's repos, so switching accounts
  // clears both, plus any open import-all wizard. Otherwise a later import could mix repos across
  // installations, or apply a branch override to a same-named repo in a different account.
  useEffect(() => {
    setSelectedRepos([]);
    setBranchOverrides({});
    setIsImportAllModalOpen(false);
  }, [activeConnectionId]);

  const {
    data: repositoryPage,
    isFetching: isRepositoriesFetching,
    isError: isRepositoriesError,
    error: repositoriesError,
  } = useGitHubAppRepositories(
    activeApp,
    {
      page, pageSize, q, sort, connectionId: activeConnectionId || undefined,
    },
    { enabled: isInstalled && !isAccountListLoading && !isAccountListError },
  );

  /*
   * Rate limiting is the failure worth calling out: searching runs against GitHub's search API,
   * whose budget is far smaller than browsing's, so search can fail while browsing works. Any
   * other failure falls back to the shared helper, which keeps the status rather than dropping it.
   */
  let repositoriesErrorMessage: string | null = null;
  if (isRepositoriesError) {
    repositoriesErrorMessage =
      hasStatus(repositoriesError) && repositoriesError.status === TOO_MANY_REQUESTS_STATUS
        ? 'Too many requests to GitHub. Please wait a moment and try again.'
        : String(errorMessageFromResponse(repositoriesError));
  }

  // isFetching (not isLoading) so revisiting a cached page/search/account also shows the
  // loader instead of stale rows while it revalidates, matching the legacy import flow.
  const shouldShowRepositoriesLoader = isRepositoriesFetching;

  const {
    data: allRepositories,
    isFetching: isAllRepositoriesFetching,
    isError: isAllRepositoriesError,
  } = useAllGitHubAppRepositories(activeApp, activeConnectionId, {
    enabled: isSelectingAll,
    q,
    // Select-all imports on the default branch, so skip the per-repo branch listing.
    excludeBranches: true,
  });
  useEffect(() => {
    if (!isSelectingAll || isAllRepositoriesFetching) {
      return;
    }
    if (allRepositories) {
      setSelectedRepos(allRepositories.map(toRepository).filter(isRepoSelectable));
      setAreAllSelected(true);
    }
    setIsSelectingAll(false);
  }, [isSelectingAll, isAllRepositoriesFetching, allRepositories]);

  /*
   * The API reports no total while a page is in flight, and can report a different one when
   * the page lands. Hold the last total it gave us so the footer keeps its shape between
   * pages, and take the new one whenever the API disagrees. Updated during render on purpose:
   * an effect would land a frame late, which is the flash this exists to remove.
   */
  const [lastKnownTotal, setLastKnownTotal] = useState(0);
  if (repositoryPage?.total !== undefined && repositoryPage.total !== lastKnownTotal) {
    setLastKnownTotal(repositoryPage.total);
  }
  const totalCount = repositoryPage?.total ?? lastKnownTotal;

  // Clamp page changes so a click past the last page is a no-op instead of landing the user
  // on an empty page. Measured against the total the footer itself renders from, so the
  // footer can never offer a page this then swallows.
  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
  const onSetPage = (newPage: number) => {
    setPage(_.clamp(newPage, 1, lastPage));
  };
  /*
   * The footer can only ever offer a page in range, but a `?page=` deep link can name one past
   * the end, and a total that shrinks (repos imported or removed) can strand the user past it.
   * Pull the page back in range once the API has actually reported a total, so the list never
   * sits on an out-of-range empty page.
   */
  useEffect(() => {
    if (repositoryPage?.total !== undefined && page > lastPage) {
      setPage(lastPage);
    }
  }, [repositoryPage?.total, page, lastPage]);

  const repositories = (repositoryPage?.results ?? []).map((row) => {
    const repo = toRepository(row);
    if (repo.locator && branchOverrides[repo.locator]) {
      repo.branch = branchOverrides[repo.locator];
    }
    return repo;
  });

  // Scope the import to the active account so the clone token and stored connection match the
  // account the listed repos belong to. `activeConnectionId` is always a real id here: import is
  // only reachable once installations have loaded and a repo is selected.
  const importRepositoriesMutation = useImportGitHubAppRepositories(activeApp, activeConnectionId);
  const isImporting = importRepositoriesMutation.isPending;
  // Read from the mutation payload so the loader count stays right after the selection clears.
  const importCount = importRepositoriesMutation.variables?.repos.length ?? 0;
  // A queued response means the batch was handed to the background bulk job; the page switches to
  // a terminal card (matching legacy) since there is no per-build feedback to show.
  const queuedCount = importRepositoriesMutation.data?.queued ?? 0;

  // Import runs through up to two gates before firing, mirroring legacy: a team-assignment modal, then
  // a quick-import scan-methods modal. `pendingRepos`/`pendingTeamIds` carry the choices between steps.
  const { data: teamsData, isLoading: isTeamsLoading } = useTeams(isInstalled);
  const allTeams = teamsData ?? [];
  const organizationId = window.user?.organizationId;
  // The scan-methods modal only renders with an organizationId, so without one the import must not
  // gate on the modal — it would open nothing and the import would never fire.
  const shouldShowScanMethods = useShouldShowQuickImportScanMethods() && Boolean(organizationId);
  const updateScanMethodDefaults = useUpdateOrganizationQuickImportScanMethodsSettings();
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [pendingRepos, setPendingRepos] = useState<Repository[]>([]);
  const [pendingTeamIds, setPendingTeamIds] = useState<number[]>([]);

  // Repos flow through the list as `Repository` but carry the `VcsRepository` fields the request
  // needs, so narrow here. The list flow passes `branchOverrides` so a branch picked after
  // selecting still reaches the request; flows without a branch picker pass no overrides.
  const toImportPayload = (
    repos: Array<Repository | VcsRepository>,
    overrides: Record<string, string>,
  ): ImportRepository[] =>
    repos.map((repo) => {
      const vcsRepo = repo as VcsRepository;
      return {
        locator: vcsRepo.locator,
        isPrivate: vcsRepo.isPrivate,
        branch: overrides[vcsRepo.locator] ?? vcsRepo.branch,
        sshCloneURL: vcsRepo.sshCloneURL,
        httpsCloneURL: vcsRepo.httpsCloneURL,
        connectableProjects: vcsRepo.connectableProjects,
      };
    });

  const runImport = (repos: Repository[], teamIds: number[], scan: Partial<ScanMethodProjectOverrides> = {}) => {
    if (repos.length === 0) {
      return;
    }
    importRepositoriesMutation.mutate(
      { repos: toImportPayload(repos, branchOverrides), teamIds, submitBadgePRs: shouldSubmitBadgePRs, ...scan },
      {
        onSuccess: (data, variables) => {
          setSelectedRepos([]);
          setAreAllSelected(false);
          setBranchOverrides({});
          openSingleImportedProject(data, variables);
        },
      },
    );
  };

  const proceedAfterTeams = (repos: Repository[], teamIds: number[]) => {
    setPendingRepos(repos);
    setPendingTeamIds(teamIds);
    if (shouldShowScanMethods) {
      setIsScanModalOpen(true);
      return;
    }
    runImport(repos, teamIds);
  };

  // Entry point for both the toolbar and per-row imports. Teams and scan methods attach only to
  // newly created projects, so a connect-only selection skips both modals.
  const beginImport = (repos: Repository[]) => {
    if (repos.length === 0 || isTeamsLoading || isImporting) {
      return;
    }
    const hasNewImports = repos.some((repo) => !repo.connectableProjects?.length);
    if (!hasNewImports) {
      runImport(repos, []);
      return;
    }
    if (teamsUserCanAddProject(allTeams).length > 0) {
      setPendingRepos(repos);
      setSelectedTeams([]);
      setIsTeamModalOpen(true);
      return;
    }
    proceedAfterTeams(repos, []);
  };

  const onImportSelected = () => beginImport(selectedRepos);

  // The import-all wizard collects everything itself (filters, policies, teams, scan methods),
  // mirroring the legacy ModalBulkImport, so its Confirm submits the wizard's enumerated repo
  // list straight to the import endpoint (which queues batches in the background), bypassing
  // the selection flow's modal chain and its sidebar badge-PR state.
  const onImportAllFinish = (repos: VcsRepository[], options: VcsImportAllOptions) => {
    if (repos.length === 0 || isImporting) {
      return;
    }
    if (options.quickImportShouldSavePreference && organizationId) {
      updateScanMethodDefaults.mutate({
        organizationId,
        projectDefaultQuickImportVendoredDetectionEnabled: options.quickImportVendoredDetectionEnabled,
        projectDefaultQuickImportSnippetDetectionEnabled: options.quickImportSnippetDetectionEnabled,
      });
    }
    importRepositoriesMutation.mutate(
      {
        // No branch overrides: the wizard exposes no branch picker, so a branch picked in the
        // list must not leak into the bulk import.
        repos: toImportPayload(repos, {}),
        teamIds: options.teamIds,
        submitBadgePRs: options.submitBadgePRs,
        quickImportVendoredDetectionEnabled: options.quickImportVendoredDetectionEnabled,
        quickImportSnippetDetectionEnabled: options.quickImportSnippetDetectionEnabled,
        policyNotifications: options.policyNotifications,
        policyUpdate: options.policyUpdate,
        policyAccess: options.policyAccess,
      },
      {
        onSuccess: (data, variables) => {
          onClearSelection();
          setBranchOverrides({});
          openSingleImportedProject(data, variables);
        },
      },
    );
  };

  const onTeamsSelectionImport = () => {
    setIsTeamModalOpen(false);
    proceedAfterTeams(pendingRepos, selectedTeams.map((team) => team.id));
  };

  const onScanMethodsConfirm = (selections: ScanMethodSelections) => {
    setIsScanModalOpen(false);
    if (selections.quickImportShouldSavePreference && organizationId) {
      updateScanMethodDefaults.mutate({
        organizationId,
        projectDefaultQuickImportVendoredDetectionEnabled: selections.quickImportVendoredDetectionEnabled,
        projectDefaultQuickImportSnippetDetectionEnabled: selections.quickImportSnippetDetectionEnabled,
      });
    }
    runImport(pendingRepos, pendingTeamIds, {
      quickImportVendoredDetectionEnabled: selections.quickImportVendoredDetectionEnabled,
      quickImportSnippetDetectionEnabled: selections.quickImportSnippetDetectionEnabled,
    });
  };

  // Keyed on `locator`: unique by construction, unlike `title`. It is always present on
  // vcs-service rows; the legacy `Repository` type just can't say so.
  const onBranchChange: OnBranchChangeFn = (repo, newBranch) => {
    const { locator } = repo;
    if (!locator) {
      return;
    }
    setBranchOverrides((current) => ({ ...current, [locator]: newBranch }));
  };

  // A new page size starts over from the first page, matching the legacy import flow.
  const onSetPageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };
  const onSelectAccount = async (_filterType: string, connectionId: string) =>
    setSelectedConnectionId(connectionId);

  // Keyed on `locator`, the same identity the rows render their checked state from: `title` is
  // only the bare repo name, so it is not unique on its own. Selection persists across pages.
  // Any hand edit means the selection is no longer "all".
  const onSelectRepository = (repo: Repository) => {
    setAreAllSelected(false);
    setSelectedRepos((current) =>
      current.some((selected) => selected.locator === repo.locator)
        ? current.filter((selected) => selected.locator !== repo.locator)
        : [...current, repo],
    );
  };
  const onSelectRepositories = (repos: Repository[], isSelected: boolean) => {
    setAreAllSelected(false);
    const locators = new Set(repos.map((repo) => repo.locator));
    setSelectedRepos((current) => {
      const rest = current.filter((selected) => !locators.has(selected.locator));
      return isSelected ? [...rest, ...repos] : rest;
    });
  };
  const onClearSelection = () => {
    setAreAllSelected(false);
    setSelectedRepos([]);
  };

  const renderMain = () => {
    if (isAccountListLoading) {
      return <Loader />;
    }
    if (isAccountListError) {
      return <ErrorAlert status={{ error: 'Failed to load GitHub App status. Please try again.' }} />;
    }
    if (isImporting) {
      return <ImportingLoader count={importCount} />;
    }
    if (queuedCount > 0) {
      // Display the submitted total, not the server's queued count: connects run inline and are
      // excluded from `queued`, but the card should match the count the user confirmed (legacy
      // shows selectedRepositories.length here too).
      return <ImportQueuedCard count={importCount} />;
    }
    if (!isInstalled) {
      return <VcsInstallPanel app={activeApp} onInstall={onInstallGithubApp} />;
    }
    return (
      <VcsRepositoryList
        repositories={repositories}
        selectedRepos={selectedRepos}
        areAllSelected={areAllSelected}
        isSelectingAll={isSelectingAll}
        accountName={activeAccount?.accountName ?? ''}
        filters={filters}
        selectedFilter={selectedFilter}
        searchValue={searchValue}
        sort={sort}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        isLoading={shouldShowRepositoriesLoader}
        error={repositoriesErrorMessage ?? (isAllRepositoriesError ? 'Please try again.' : null)}
        isImportBusy={isImporting || isTeamsLoading || isSelectingAll}
        onSearchChange={setSearchValue}
        onSortChange={setSort}
        onSelectAccount={onSelectAccount}
        onInstallGithubApp={onInstallGithubApp}
        onSetPage={onSetPage}
        onSetPageSize={onSetPageSize}
        onSelectRepository={onSelectRepository}
        onSelectRepositories={onSelectRepositories}
        onSelectAll={() => setIsSelectingAll(true)}
        onClearSelection={onClearSelection}
        onBranchChange={onBranchChange}
        onImportSelected={onImportSelected}
        onImportAll={() => {
          // The wizard hides its teams step when `allTeams` is empty, so opening it before
          // teams load could skip required team assignment.
          if (!isTeamsLoading) {
            setIsImportAllModalOpen(true);
          }
        }}
      />
    );
  };

  return (
    <div className={styles.page}>
      <Helmet title={PAGE_TITLE[activeApp]} titleTemplate="%s | FOSSA" />
      <VcsImportHeader app={activeApp} />
      <div className={`container container-responsive ${styles.body}`}>
        <div className={styles.layout}>
          <VcsImportSidebar
            app={activeApp}
            isFossabotInstalled={hasFossabotAccounts}
            isFossaCoreInstalled={hasFossaCoreAccounts}
            shouldSubmitBadgePRs={shouldSubmitBadgePRs}
            onToggleSubmitBadgePRs={() => setShouldSubmitBadgePRs((current) => !current)}
          />
          <main className={styles.main}>{renderMain()}</main>
        </div>
      </div>
      <ModalTeamAssignment
        show={isTeamModalOpen}
        onHide={() => setIsTeamModalOpen(false)}
        allTeams={allTeams}
        selectedTeams={selectedTeams}
        onTeamsSelected={setSelectedTeams}
        onTeamsSelectionImport={onTeamsSelectionImport}
        hasNextStep={shouldShowScanMethods}
      />
      {organizationId && (
        <QuickImportScanMethodsModal
          organizationId={organizationId}
          show={isScanModalOpen}
          onBack={() => setIsScanModalOpen(false)}
          onConfirm={onScanMethodsConfirm}
        />
      )}
      <VcsImportAllModal
        show={isImportAllModalOpen}
        app={activeApp}
        accountName={activeAccount?.accountName ?? ''}
        connectionId={activeConnectionId}
        allTeams={allTeams}
        onHide={() => setIsImportAllModalOpen(false)}
        onFinish={onImportAllFinish}
      />
      <VcsConnectAccountModal
        show={connectStatus !== null && !isAccountListLoading && !isAccountListError}
        app={activeApp}
        hasAuthorizationError={connectStatus === 'error'}
        onHide={() => setConnectStatus(null)}
        onInstallAnother={onInstallAnotherAccount}
      />
    </div>
  );
};
